/**
 * ActiveCallScreen — full-screen in-call UI.
 *
 * Shown whenever sdkStatus is "ringing" or "incall", replacing the normal
 * tab content. Mirrors the CloudTalk-style layout:
 *   • Timer pill + Recording indicator at the top (grey shell area)
 *   • Number / contact name, local time, and "via" outbound number in the centre
 *   • White floating card at the bottom with 6 action buttons
 *
 * Live API integrations:
 *   • Tags: fetches account tags (GET /tags) and lets the user select / create
 *     new ones (POST /tags). If a contact match is found, saving tags patches
 *     the contact (PATCH /contacts/:id). Otherwise tags are held in state and
 *     applied when the contact is created.
 *   • Notes: textarea saved to the matched contact on Save (PATCH /contacts/:id).
 *     Held in state when no contact exists yet.
 *   • Add contact: checks if the remote number is already a contact
 *     (GET /contacts?search=…) on mount. If not, opens a form that posts to
 *     POST /contacts with the phone, tags, and notes pre-filled.
 *
 * Hold is implemented as mic-mute + visual state (the AT WebRTC SDK does not
 * expose a native SIP hold/resume method).
 * Transfer is shown disabled — requires server-side SIP support.
 */

import {
  Mic,
  MicOff,
  Pause,
  ArrowRightLeft,
  Tag,
  FileText,
  UserPlus,
  Phone,
  Check,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ActiveCallInfo } from "../hooks/useATClient";
import { BottomSheet } from "../components/BottomSheet";
import { cn } from "../lib/cn";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function useCallTimer(answeredAt: string | null): string {
  const [elapsed, setElapsed] = useState("0:00");
  useEffect(() => {
    if (!answeredAt) {
      setElapsed("0:00");
      return;
    }
    const update = () => {
      const secs = Math.floor((Date.now() - new Date(answeredAt).getTime()) / 1000);
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      setElapsed(`${m}:${String(s).padStart(2, "0")}`);
    };
    update();
    const id = setInterval(update, 1_000);
    return () => clearInterval(id);
  }, [answeredAt]);
  return elapsed;
}

function useLocalTime(): string {
  const fmt = () =>
    new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  const [time, setTime] = useState(fmt);
  useEffect(() => {
    const id = setInterval(() => setTime(fmt()), 30_000);
    return () => clearInterval(id);
  }, []);
  const offset = -new Date().getTimezoneOffset() / 60;
  const sign = offset >= 0 ? "+" : "-";
  return `${time} (GMT${sign}${Math.abs(offset)})`;
}

// ─── Tag colour mapping ───────────────────────────────────────────────────────

const TAG_COLORS: Record<string, { pill: string; pillSelected: string }> = {
  blue: {
    pill: "bg-blue-50 text-blue-600 border-blue-200",
    pillSelected: "bg-blue-500 text-white border-blue-500",
  },
  green: {
    pill: "bg-green-50 text-green-600 border-green-200",
    pillSelected: "bg-green-500 text-white border-green-500",
  },
  purple: {
    pill: "bg-purple-50 text-purple-600 border-purple-200",
    pillSelected: "bg-purple-500 text-white border-purple-500",
  },
  orange: {
    pill: "bg-orange-50 text-orange-600 border-orange-200",
    pillSelected: "bg-orange-500 text-white border-orange-500",
  },
  pink: {
    pill: "bg-pink-50 text-pink-600 border-pink-200",
    pillSelected: "bg-pink-500 text-white border-pink-500",
  },
  teal: {
    pill: "bg-teal-50 text-teal-600 border-teal-200",
    pillSelected: "bg-teal-500 text-white border-teal-500",
  },
  red: {
    pill: "bg-red-50 text-red-600 border-red-200",
    pillSelected: "bg-red-500 text-white border-red-500",
  },
  amber: {
    pill: "bg-amber-50 text-amber-600 border-amber-200",
    pillSelected: "bg-amber-500 text-white border-amber-500",
  },
};

function tagColorClass(color: string, selected: boolean): string {
  return (TAG_COLORS[color] ?? TAG_COLORS["blue"]!)[selected ? "pillSelected" : "pill"];
}

// ─── Action button ────────────────────────────────────────────────────────────

interface ActionButtonProps {
  icon: React.ElementType;
  label: string;
  active?: boolean;
  disabled?: boolean;
  badge?: number | undefined;
  onClick: () => void;
}

function ActionButton({
  icon: Icon,
  label,
  active = false,
  disabled = false,
  badge,
  onClick,
}: ActionButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "relative flex flex-col items-center gap-1.5 rounded-xl p-2 transition-colors",
        disabled
          ? "cursor-not-allowed opacity-30"
          : active
            ? "text-primary"
            : "text-foreground hover:bg-muted/50 active:bg-muted",
      )}
    >
      {badge !== undefined && badge > 0 && (
        <span className="absolute right-2 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
          {badge}
        </span>
      )}
      <div
        className={cn(
          "flex h-12 w-12 items-center justify-center rounded-full",
          active ? "bg-primary/15" : "bg-muted",
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <span className="text-[11px] font-medium leading-none text-muted-foreground">{label}</span>
    </button>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface AccountTag {
  id: string;
  name: string;
  color: string;
}

interface ContactMatch {
  id: string;
  displayName: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface ActiveCallScreenProps {
  isRinging: boolean;
  isInCall: boolean;
  activeCall: ActiveCallInfo;
  isMuted: boolean;
  isOnHold: boolean;
  /** The outbound number the user is calling from ("via …"). */
  outboundNumber: string | null;
  apiUrl: string;
  accessToken: string | null;
  onMute: () => void;
  onUnmute: () => void;
  onHold: () => void;
  onUnhold: () => void;
}

export function ActiveCallScreen({
  isRinging,
  isInCall,
  activeCall,
  isMuted,
  isOnHold,
  outboundNumber,
  apiUrl,
  accessToken,
  onMute,
  onUnmute,
  onHold,
  onUnhold,
}: ActiveCallScreenProps) {
  const timer = useCallTimer(activeCall.answeredAt);
  const localTime = useLocalTime();

  // ── Notes state (declared early so the contact lookup effect can seed it) ───
  const [notes, setNotes] = useState("");

  // ── Contact lookup ──────────────────────────────────────────────────────────
  // "unknown" = still checking, null = confirmed not a contact, object = found.
  const [contactMatch, setContactMatch] = useState<ContactMatch | null | "unknown">("unknown");

  useEffect(() => {
    if (!accessToken || !activeCall.remoteNumber) {
      setContactMatch(null);
      return;
    }
    void (async () => {
      try {
        const res = await fetch(
          `${apiUrl}/contacts?search=${encodeURIComponent(activeCall.remoteNumber)}&limit=1`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (!res.ok) {
          setContactMatch(null);
          return;
        }
        const data = (await res.json()) as {
          contacts: Array<{
            id: string;
            firstName: string | null;
            lastName: string | null;
            tags: string[];
            notes: string | null;
          }>;
        };
        const match = data.contacts[0];
        if (match) {
          const parts = [match.firstName, match.lastName].filter(Boolean);
          setContactMatch({
            id: match.id,
            displayName: parts.length > 0 ? parts.join(" ") : activeCall.remoteNumber,
          });
          // Seed notes from the existing contact.
          setNotes(match.notes ?? "");
          // Store tag names so selectedTagIds can be seeded once accountTags loads.
          initialContactTagNamesRef.current = match.tags ?? [];
        } else {
          setContactMatch(null);
        }
      } catch {
        setContactMatch(null);
      }
    })();
  }, [activeCall.remoteNumber, apiUrl, accessToken]);

  // ── Account tags ───────────────────────────────────────────────────────────
  const [accountTags, setAccountTags] = useState<AccountTag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [newTagName, setNewTagName] = useState("");
  const [creatingTag, setCreatingTag] = useState(false);
  const newTagInputRef = useRef<HTMLInputElement>(null);
  // Tag names from the matched contact — used to pre-select tags once accountTags loads.
  const initialContactTagNamesRef = useRef<string[]>([]);
  const tagsSeededRef = useRef(false);

  // Fetch account tags once when we have a token.
  useEffect(() => {
    if (!accessToken) return;
    setTagsLoading(true);
    void fetch(`${apiUrl}/tags`, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((r) => (r.ok ? r.json() : { tags: [] }))
      .then((d: { tags?: AccountTag[] }) => setAccountTags(d.tags ?? []))
      .finally(() => setTagsLoading(false));
  }, [apiUrl, accessToken]);

  // Seed selectedTagIds from the matched contact once both fetches have completed.
  // The contact lookup and accountTags fetch are concurrent so we wait for both.
  useEffect(() => {
    if (tagsSeededRef.current) return;
    if (contactMatch === "unknown" || tagsLoading) return; // either fetch still in flight
    tagsSeededRef.current = true;
    if (initialContactTagNamesRef.current.length === 0) return;
    const matchedIds = accountTags
      .filter((t) => initialContactTagNamesRef.current.includes(t.name))
      .map((t) => t.id);
    setSelectedTagIds(matchedIds);
  }, [contactMatch, tagsLoading, accountTags]);

  const toggleTag = (id: string) => {
    setSelectedTagIds((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  };

  const createTag = async () => {
    const name = newTagName.trim();
    if (!name || !accessToken || creatingTag) return;
    setCreatingTag(true);
    try {
      const res = await fetch(`${apiUrl}/tags`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ name }),
      });
      if (res.ok) {
        const data = (await res.json()) as { tag: AccountTag };
        setAccountTags((prev) => [...prev, data.tag].sort((a, b) => a.name.localeCompare(b.name)));
        setSelectedTagIds((prev) => [...prev, data.tag.id]);
        setNewTagName("");
        newTagInputRef.current?.focus();
      }
    } finally {
      setCreatingTag(false);
    }
  };

  // Save selected tags to an existing contact.
  const [savingTags, setSavingTags] = useState(false);
  const saveTagsToContact = async (contactId: string) => {
    if (!accessToken) return;
    const selectedNames = accountTags
      .filter((t) => selectedTagIds.includes(t.id))
      .map((t) => t.name);
    setSavingTags(true);
    try {
      await fetch(`${apiUrl}/contacts/${contactId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ tags: selectedNames }),
      });
    } finally {
      setSavingTags(false);
    }
  };

  // ── Notes ──────────────────────────────────────────────────────────────────
  // Declared here (after tags) but state is seeded earlier in the contact lookup effect.
  const [savingNotes, setSavingNotes] = useState(false);

  const saveNotes = async () => {
    if (!accessToken) return;
    const match = typeof contactMatch === "object" && contactMatch !== null ? contactMatch : null;
    if (!match) return; // held locally until contact is created
    setSavingNotes(true);
    try {
      await fetch(`${apiUrl}/contacts/${match.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ notes }),
      });
    } finally {
      setSavingNotes(false);
    }
  };

  // ── Add contact ────────────────────────────────────────────────────────────
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [savingContact, setSavingContact] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  const submitContact = async () => {
    if (!accessToken || savingContact) return;
    setContactError(null);
    setSavingContact(true);
    const selectedTagNames = accountTags
      .filter((t) => selectedTagIds.includes(t.id))
      .map((t) => t.name);
    try {
      const res = await fetch(`${apiUrl}/contacts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({
          phone: activeCall.remoteNumber,
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          email: email.trim() || undefined,
          tags: selectedTagNames.length > 0 ? selectedTagNames : undefined,
          notes: notes.trim() || undefined,
          source: "dialer",
        }),
      });
      if (res.ok) {
        const data = (await res.json()) as {
          contact: { id: string; firstName: string | null; lastName: string | null };
        };
        const parts = [data.contact.firstName, data.contact.lastName].filter(Boolean);
        setContactMatch({
          id: data.contact.id,
          displayName: parts.length > 0 ? parts.join(" ") : activeCall.remoteNumber,
        });
        setAddContactOpen(false);
      } else {
        const errData = (await res.json()) as { error?: string };
        setContactError(errData.error ?? "Failed to save contact");
      }
    } catch {
      setContactError("Network error — please try again");
    } finally {
      setSavingContact(false);
    }
  };

  // ── Modal open states ──────────────────────────────────────────────────────
  const [tagsOpen, setTagsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  // Convenience: the contact match as an object or null.
  const resolvedContact =
    typeof contactMatch === "object" && contactMatch !== null ? contactMatch : null;

  // Display name: prefer contact name if matched, else show the raw number.
  const displayName = resolvedContact?.displayName ?? activeCall.remoteNumber;

  return (
    <div className="flex flex-1 select-none flex-col overflow-hidden">
      {/* ── Grey shell area — call info ───────────────────────────────────── */}
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6">
        {/* Timer pill */}
        <div
          className={cn(
            "flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold shadow-sm",
            isInCall ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
          )}
        >
          <Phone className="h-4 w-4 fill-current" />
          <span>{isRinging ? "Ringing…" : timer}</span>
        </div>

        {/* Recording indicator */}
        {isInCall && (
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 animate-pulse rounded-full bg-destructive" />
            <span className="text-xs font-medium text-muted-foreground">Recording</span>
          </div>
        )}

        {/* Remote number / contact name */}
        <p className="text-2xl font-semibold tracking-tight text-foreground">{displayName}</p>

        {/* Show raw number underneath if we have a contact name */}
        {resolvedContact && (
          <p className="text-xs text-muted-foreground">{activeCall.remoteNumber}</p>
        )}

        {/* Local time */}
        <p className="text-xs text-muted-foreground">{localTime}</p>

        {/* Via outbound number */}
        {outboundNumber && <p className="text-xs text-muted-foreground">via 🇳🇬 {outboundNumber}</p>}

        {/* On-hold badge */}
        {isOnHold && (
          <div className="rounded-full bg-amber-100 px-4 py-1 text-xs font-semibold text-amber-700">
            Call on hold
          </div>
        )}
      </div>

      {/* ── Floating action card ───────────────────────────────────────────── */}
      <div className="mx-3 mb-3 overflow-hidden rounded-2xl bg-card shadow-sm">
        <div className="grid grid-cols-3 gap-y-1 p-3">
          {/* Row 1 */}
          <ActionButton
            icon={isMuted && !isOnHold ? MicOff : Mic}
            label={isMuted && !isOnHold ? "Unmute" : "Mute"}
            active={isMuted && !isOnHold}
            disabled={!(isRinging || isInCall) || isOnHold}
            onClick={isMuted ? onUnmute : onMute}
          />
          <ActionButton
            icon={Pause}
            label={isOnHold ? "Resume" : "Hold"}
            active={isOnHold}
            disabled={!isInCall}
            onClick={isOnHold ? onUnhold : onHold}
          />
          <ActionButton icon={ArrowRightLeft} label="Transfer" disabled onClick={() => {}} />

          {/* Row 2 */}
          <ActionButton
            icon={Tag}
            label="Tags"
            active={selectedTagIds.length > 0}
            disabled={!(isRinging || isInCall) || !resolvedContact}
            onClick={() => setTagsOpen(true)}
          />
          <ActionButton
            icon={FileText}
            label="Notes"
            active={notes.length > 0}
            disabled={!(isRinging || isInCall) || !resolvedContact}
            onClick={() => setNotesOpen(true)}
          />
          <ActionButton
            icon={resolvedContact ? Check : UserPlus}
            label={resolvedContact ? "Saved" : "Add contact"}
            active={resolvedContact !== null}
            disabled={resolvedContact !== null || contactMatch === "unknown"}
            onClick={() => setAddContactOpen(true)}
          />
        </div>
      </div>

      {/* ── Tags modal ────────────────────────────────────────────────────── */}
      <BottomSheet
        open={tagsOpen}
        onClose={() => {
          // If there's a matched contact and tags were selected, save them.
          if (resolvedContact && selectedTagIds.length > 0) {
            void saveTagsToContact(resolvedContact.id);
          }
          setTagsOpen(false);
        }}
        title="Tag this call"
      >
        <div className="flex flex-col gap-4 px-5 pb-6">
          {tagsLoading ? (
            <p className="text-sm text-muted-foreground">Loading tags…</p>
          ) : accountTags.length === 0 && selectedTagIds.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No tags yet. Create your first one below.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {accountTags.map((tag) => {
                const selected = selectedTagIds.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    onClick={() => toggleTag(tag.id)}
                    className={cn(
                      "flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors",
                      tagColorClass(tag.color, selected),
                    )}
                  >
                    {selected && <Check className="h-3 w-3" />}
                    {tag.name}
                  </button>
                );
              })}
            </div>
          )}

          {/* Create new tag */}
          <div className="flex gap-2">
            <input
              ref={newTagInputRef}
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void createTag();
                }
              }}
              placeholder="Create a new tag…"
              className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
            <button
              onClick={() => void createTag()}
              disabled={!newTagName.trim() || creatingTag}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-40"
            >
              {creatingTag ? "…" : "Add"}
            </button>
          </div>

          {/* Save button (if contact exists) */}
          {resolvedContact && selectedTagIds.length > 0 && (
            <button
              onClick={async () => {
                await saveTagsToContact(resolvedContact.id);
                setTagsOpen(false);
              }}
              disabled={savingTags}
              className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {savingTags ? "Saving…" : "Save tags to contact"}
            </button>
          )}
        </div>
      </BottomSheet>

      {/* ── Notes modal ───────────────────────────────────────────────────── */}
      <BottomSheet open={notesOpen} onClose={() => setNotesOpen(false)} title="Call notes">
        <div className="flex flex-col gap-3 px-5 pb-6">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              resolvedContact
                ? `Notes for ${resolvedContact.displayName}…`
                : "Write notes about this call…"
            }
            rows={5}
            className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
          />
          {resolvedContact && (
            <button
              onClick={async () => {
                await saveNotes();
                setNotesOpen(false);
              }}
              disabled={savingNotes}
              className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {savingNotes ? "Saving…" : "Save notes"}
            </button>
          )}
          {!resolvedContact && (
            <p className="text-center text-xs text-muted-foreground">
              Notes will be saved when you add this number as a contact.
            </p>
          )}
        </div>
      </BottomSheet>

      {/* ── Add contact modal ─────────────────────────────────────────────── */}
      <BottomSheet
        open={addContactOpen}
        onClose={() => {
          setAddContactOpen(false);
          setContactError(null);
        }}
        title="Add contact"
      >
        <div className="flex flex-col gap-3 px-5 pb-6">
          {/* Phone (read-only, pre-filled) */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Phone
            </label>
            <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
              {activeCall.remoteNumber}
            </div>
          </div>

          {/* Name fields */}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                First name
              </label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Last name
              </label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
          </div>

          {/* Show any selected tags */}
          {selectedTagIds.length > 0 && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Tags
              </label>
              <div className="flex flex-wrap gap-1.5">
                {accountTags
                  .filter((t) => selectedTagIds.includes(t.id))
                  .map((t) => (
                    <span
                      key={t.id}
                      className={cn(
                        "rounded-full border px-2.5 py-0.5 text-xs font-medium",
                        tagColorClass(t.color, true),
                      )}
                    >
                      {t.name}
                    </span>
                  ))}
              </div>
            </div>
          )}

          {/* Show notes preview */}
          {notes.trim().length > 0 && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Notes
              </label>
              <p className="line-clamp-2 text-xs text-muted-foreground">{notes}</p>
            </div>
          )}

          {/* Error */}
          {contactError && (
            <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {contactError}
            </p>
          )}

          <button
            onClick={() => void submitContact()}
            disabled={savingContact}
            className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {savingContact ? "Saving…" : "Save contact"}
          </button>
        </div>
      </BottomSheet>
    </div>
  );
}
